import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Icon } from '@/common/components/Icon'
import Loader from '@/common/components/Loader'
import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox } from '@/components/ui'
import { IColumn, Table } from '@/core/components/Table'
import { useCheckPermission } from '@/core/hooks/use-check-permission'
import { PartnerDto, PartnerUpsertDto } from '@/common/models/gamification'
import {
  createPartner,
  deletePartner,
  getPartners,
  updatePartner,
} from '@/core/services/gamification'

import { MessageBox } from '../messages/MessageBox'
import { CreatePartnerModal } from './CreatePartnerModal'

export const PartnersPage: React.FC = () => {
  const { editionId } = useParams()
  const { hasEditPermission } = useCheckPermission({ page: 'GAMIFICATION' })

  const [partners, setPartners] = useState<PartnerDto[]>([])
  const [loading, setLoading] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<PartnerDto | null>(null)

  const [isError, setIsError] = useState(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>()
  const [showMessageBox, setShowMessageBox] = useState(false)
  const [message, setMessage] = useState('')

  const handleError = (e: unknown) => {
    setIsError(true)
    setShowMessageBox(true)
    setMessage(e instanceof Error ? e.message : 'Something went wrong')
    setErrorStatus((e as { response?: { status?: number } })?.response?.status)
  }

  const notify = (msg: string) => {
    setIsError(false)
    setMessage(msg)
    setShowMessageBox(true)
  }

  const getData = useCallback(async () => {
    if (!editionId) return
    try {
      setLoading(true)
      const { data } = await getPartners(editionId)
      setPartners(data ?? [])
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }, [editionId])

  useEffect(() => {
    getData()
  }, [getData])

  const openCreate = () => {
    setEditing(null)
    setCreateOpen(true)
  }

  const openEdit = (row: PartnerDto) => {
    if (!hasEditPermission) return
    setEditing(row)
    setCreateOpen(true)
  }

  const handleSubmit = async (values: PartnerUpsertDto, id?: string) => {
    if (!editionId) return
    try {
      setLoading(true)
      if (id) {
        await updatePartner(id, values)
        notify('Partner updated successfully')
      } else {
        await createPartner(editionId, values)
        notify('Partner created successfully')
      }
      await getData()
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      setLoading(true)
      await deletePartner(id)
      notify('Partner deleted successfully')
      await getData()
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }

  const columns: IColumn[] = useMemo(
    () => [
      {
        title: 'Name',
        accessor: 'name',
        render: (row: PartnerDto) => (
          <span className="text-monochrome-1">{row.name}</span>
        ),
      },
      {
        title: 'Active',
        accessor: 'active',
        render: (row: PartnerDto) => (
          <Checkbox checked={row.active} disabled />
        ),
      },
      {
        title: 'Order',
        accessor: 'ordinal',
        render: (row: PartnerDto) => (
          <span className="text-monochrome-2">{row.ordinal}</span>
        ),
      },
      {
        title: 'Edit',
        accessor: '',
        render: (row: PartnerDto) => (
          <a
            className="view text-accent-1 underline"
            href="#"
            onClick={(e) => {
              e.preventDefault()
              openEdit(row)
            }}
          >
            Edit
          </a>
        ),
      },
    ],
    [hasEditPermission],
  )

  return (
    <>
      <Header
        pageTitle="Gamification"
        actions={
          hasEditPermission && (
            <Button variant="primary-bordered" onClick={openCreate}>
              Add Partner
              <Icon icon="plus-circle" width="18" height="18" viewBox="0 0 18 18" />
            </Button>
          )
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle icon="heart" title="Partners" className="mb-5" />
        <Table
          className="[&_th]:!whitespace-nowrap"
          columns={columns}
          data={partners}
          noDataMessage="No partners yet"
        />
      </Card>

      <CreatePartnerModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        partner={editing}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
      />

      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
